import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { adminGetSurvey } from '../../lib/adminApi';

function RatingInput({ question, value, onChange }) {
  const max = question.question_type === 'rating_10' ? 10 : 5;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {Array.from({ length: max }, (_, i) => i + 1).map(n => (
        <button key={n} onClick={() => onChange(n)} style={{
          width: 42, height: 42, borderRadius: 8, border: '2px solid',
          borderColor: value === n ? '#0176CE' : '#e2e8f0',
          background: value === n ? '#0176CE' : '#fff',
          color: value === n ? '#fff' : '#374151',
          fontWeight: 700, fontSize: 15, cursor: 'pointer',
        }}>{n}</button>
      ))}
    </div>
  );
}

function QuestionInput({ question, value, onChange }) {
  const { question_type, options } = question;
  if (question_type === 'rating_10' || question_type === 'rating_5')
    return <RatingInput question={question} value={value} onChange={onChange} />;
  if (question_type === 'yes_no')
    return (
      <div style={{ display: 'flex', gap: 12 }}>
        {['Yes', 'No'].map(opt => (
          <button key={opt} onClick={() => onChange(opt)} style={{
            padding: '10px 28px', borderRadius: 8, border: '2px solid', cursor: 'pointer', fontWeight: 600, fontSize: 14,
            borderColor: value === opt ? '#0176CE' : '#e2e8f0',
            background: value === opt ? '#0176CE' : '#fff',
            color: value === opt ? '#fff' : '#374151',
          }}>{opt}</button>
        ))}
      </div>
    );
  if (question_type === 'multiple_choice' && options)
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
            <input type="radio" checked={value === opt} onChange={() => onChange(opt)}
              style={{ accentColor: '#0176CE', width: 16, height: 16 }} />
            {opt}
          </label>
        ))}
      </div>
    );
  if (question_type === 'multiple_choice_multi' && options) {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map(opt => (
          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
            <input type="checkbox" checked={selected.includes(opt)}
              onChange={() => onChange(selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt])}
              style={{ accentColor: '#0176CE', width: 16, height: 16 }} />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  return (
    <textarea rows={4} placeholder="Your answer…" value={value || ''}
      onChange={e => onChange(e.target.value)}
      style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, color: '#374151', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
  );
}

export default function SurveyPreview() {
  const { id } = useParams();
  const [survey, setSurvey]   = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminGetSurvey(id)
      .then(r => { setSurvey(r.survey); setQuestions(r.questions); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={s.outer}><p style={{ color: '#6b7280', textAlign: 'center', marginTop: 80 }}>Loading…</p></div>;

  return (
    <div style={s.outer}>
      {/* Preview banner */}
      <div style={s.banner}>
        PREVIEW MODE — this is how clients will see the survey
      </div>

      <div style={s.card}>
        <div style={s.logo}>MasterFileAI</div>

        <h1 style={s.heading}>Hi Mary,</h1>
        <p style={s.intro}>
          Thank you for taking the time to share your feedback — it means a lot to us and helps us improve.
          This survey is about your recent file: <strong>your_vendors.csv</strong>.
        </p>
        {survey?.description && <p style={s.desc}>{survey.description}</p>}

        <div style={{ marginTop: 32 }}>
          {questions.map((q, idx) => (
            <div key={q.question_id} style={s.qBlock}>
              <p style={s.qText}>
                <span style={s.qNum}>{idx + 1}</span>
                {q.question_text}
                {q.required && <span style={{ color: '#dc2626', marginLeft: 4 }}>*</span>}
              </p>
              <QuestionInput question={q} value={answers[q.question_id]} onChange={v => setAnswers(a => ({ ...a, [q.question_id]: v }))} />
            </div>
          ))}
        </div>

        <button style={{ ...s.submitBtn, opacity: 0.6, cursor: 'not-allowed' }} disabled>
          Submit Feedback (disabled in preview)
        </button>

        <p style={s.footer}>
          This survey was sent personally to Mary Smith at Acme Corp.
        </p>
      </div>
    </div>
  );
}

const s = {
  outer:     { minHeight: '100vh', background: '#f0f4f8', paddingBottom: 60 },
  banner:    { background: '#f59e0b', color: '#fff', fontWeight: 700, fontSize: 13, textAlign: 'center', padding: '10px', letterSpacing: '0.05em' },
  card:      { background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '40px 44px', width: '100%', maxWidth: 640, margin: '32px auto 0', boxSizing: 'border-box' },
  logo:      { background: '#0176CE', color: '#fff', fontWeight: 700, fontSize: 16, padding: '8px 16px', borderRadius: 8, display: 'inline-block', marginBottom: 28 },
  heading:   { fontSize: 26, fontWeight: 700, color: '#0a2540', margin: '0 0 12px' },
  intro:     { fontSize: 15, color: '#374151', lineHeight: 1.7, margin: '0 0 8px' },
  desc:      { fontSize: 14, color: '#6b7280', lineHeight: 1.6, background: '#f8fafc', borderRadius: 8, padding: '12px 16px', margin: '12px 0 0' },
  qBlock:    { marginBottom: 32 },
  qText:     { fontSize: 15, fontWeight: 600, color: '#0a2540', margin: '0 0 14px', lineHeight: 1.5, display: 'flex', alignItems: 'flex-start', gap: 10 },
  qNum:      { background: '#0176CE', color: '#fff', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 1 },
  submitBtn: { width: '100%', background: '#0176CE', border: 'none', borderRadius: 10, color: '#fff', fontSize: 16, fontWeight: 700, padding: '15px', marginTop: 8 },
  footer:    { fontSize: 12, color: '#9ca3af', textAlign: 'center', marginTop: 20, lineHeight: 1.5 },
};
